import { isEmpty, isNullish } from 'remeda'
import { dataTestIds } from '@velasca/constants'
import { useHeader } from '@velasca/hooks/use-header'
import { useLiquidSkeleton } from '@velasca/hooks/use-liquid-skeleton'
import { Button } from '@velasca/ui/button'
import { PageIntro } from '@velasca/ui/page-intro'
import { buildIntrinsicWidthSources } from '@velasca/utils/build-intrinsic-width-sources'
import { getMediaSources } from '@velasca/utils/get-media-sources'
import { setQueryParams } from '@velasca/utils/set-query-params'
import {
  ButtonContainer,
  CurvedTextWrapper,
  TextContainer,
  TextContent,
  Title,
  TopImage,
} from './Hero.styles'
import type { HeroProps } from './Hero.types'

export const Hero = ({
  topImage,
  curvedText,
  title,
  ctaUrl,
  ctaText,
  backgroundMobileImage,
  backgroundDesktopImage,
}: HeroProps): JSX.Element => {
  useLiquidSkeleton('#hero')
  const offsetHeight = useHeader(({ headerHeight }) => headerHeight)
  const mediaSources = getMediaSources(
    backgroundMobileImage,
    backgroundDesktopImage,
  )

  const fallbackImage = mediaSources[0] ?? null

  if (!fallbackImage) {
    throw new Error('no fallback image available')
  }

  return (
    <PageIntro
      canScroll={false}
      mediaSources={mediaSources}
      fallbackImage={fallbackImage}
      offsetHeight={offsetHeight}
      fetchPriority="high"
    >
      <TextContainer>
        <TextContent>
          {!isNullish(topImage) && !isEmpty(topImage) && (
            <TopImage
              sources={buildIntrinsicWidthSources({ src: topImage.src })}
              alt={topImage.alt}
              imgSrc={setQueryParams(topImage.src, [
                { name: 'width', value: '30' },
              ])}
            />
          )}
          {curvedText && (
            <CurvedTextWrapper viewBox="0 0 300 70">
              <path id="curve" d="M 25,70 C 75,5 225,5 275,70" />
              <text width="100%" textLength="282">
                <textPath textLength="282" xlinkHref="#curve">
                  {curvedText}
                </textPath>
              </text>
            </CurvedTextWrapper>
          )}
          {title && <Title dangerouslySetInnerHTML={{ __html: title }} />}
          {ctaText && ctaUrl && (
            <ButtonContainer>
              <Button
                fullWidth
                as="a"
                variant="ghostWhite"
                href={ctaUrl}
                text={ctaText}
                dataTestId={dataTestIds.HOMEPAGE_HERO_CTA}
              />
            </ButtonContainer>
          )}
        </TextContent>
      </TextContainer>
    </PageIntro>
  )
}
